import React, { useEffect, useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Col, Row, Space, Form, Input, Tabs, Card, Button, message, Alert, Checkbox, Typography, Modal, InputNumber, Popconfirm, Tooltip, Segmented, Dropdown, Switch } from 'antd';
import { PlusOutlined, ToolOutlined, CopyOutlined, DeleteOutlined, RocketOutlined, GiftOutlined, AppstoreOutlined, TableOutlined, ExpandAltOutlined, ShrinkOutlined, ImportOutlined, MoreOutlined, InfoCircleOutlined, FilterOutlined, SyncOutlined } from '@ant-design/icons';
import { useApolloClient } from 'react-apollo';
import GET_NEXXEN_LINE_ITEMS from '../../../core/GraphQl/Queries/GET_NEXXEN_LINE_ITEMS';
import moment from 'moment';
import { usePermify } from '@permify/react-role';
import { GoogleApiWrapper } from 'google-maps-react';

import CampaignRequestorSummary from './CampaignRequestorSummaryOptimized';
import CampaignRequestorDetails from './CampaignRequestorDetails';
import CampaignRequestorBudget from './CampaignRequestorBudget';
import CampaignRequestorCategories from './CampaignRequestorCategories';
import CampaignRequestorLineItem from './CampaignRequestorLineItem';
import CampaignRequestorLineItemTable from './CampaignRequestorLineItemTable';
import CampaignRequestorNotes from './CampaignRequestorNotes';
import CampaignRequestorAttributionReporting from './CampaignRequestorAttributionReporting';
import CampaignRequestorComplianceAudiences from './CampaignRequestorComplianceAudiences';
import { CampaignOrderQueueHistory } from '../CampaignOrderQueue/components/CampaignOrderQueueHistory';
import { CampaignOrderQueueProvider } from '../../../core/components/campaigns/campaignordersqueue/CampaingOrderQueueProvider';
import OverlaySpinner from '../../shared/OverlaySpinner';
import { useCampaignForm } from '../../../core/hooks/useCampaignForm';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { campaignTypeMap, defaultIabCategory } from '../../../core/utils/constants/constants';
import googleMapsConfig from '../../../platform/shared/maps/googlemaps';
import { CampaignConfig } from '../Campaigns/shared/configs/campaignConfig';
import { handleInitialValues } from './utils/formInitialization';
import CampaignHeader from './components/CampaignHeader';
import CampaignBudgetAllocation from './components/CampaignBudgetAllocation';
import LineItemImportModal from './components/LineItemImportModal';
import { getCampaignCountConstraints } from './utils/campaignValidation';
import { normalizeLineItemPercentages, createFlightSchedulesWithBudget } from './utils/lineItemImport.utils';
import { getCpmForCampaignType, getDefaultCpm as getDefaultCpmHelper, getPercentageForAdvertiser } from '../../../core/utils/pricing/cpmHelper';
import { getPricingFieldsForSave } from '../../../core/utils/pricing/pricingFieldMapper';
import { useTimezone } from '../../../core/hooks/useTimezone';
import { useCampaignSaveManager } from './hooks/useCampaignSaveManager';

const { Text } = Typography;

const CampaignRequestorEdit = ({ google }) => {
  const history = useHistory();
  const { id } = useParams();
  const mode = 'edit'; // Fixed to edit mode
  const cpmInitializedRef = useRef(false);
  const { isAuthorized } = usePermify();
  const { timezone } = useTimezone();
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [showOnlyDeliveryItems, setShowOnlyDeliveryItems] = useState(false);
  const [isBulkCloneMode, setIsBulkCloneMode] = useState(false);
  const [selectedLineItemIndices, setSelectedLineItemIndices] = useState({});
  const [hasTraffickerOrProcessorPermission, setHasTraffickerOrProcessorPermission] = useState(false);
  const [isNotDraftStatus, setIsNotDraftStatus] = useState(false);
  const [lineItemViewModes, setLineItemViewModes] = useState({}); // { campaignIndex: 'table' | 'card' }
  const [expandedLineItems, setExpandedLineItems] = useState({}); // { campaignIndex: [lineItemIndex, ...] }
  const [isAddLineItemModalVisible, setIsAddLineItemModalVisible] = useState(false);
  const [addLineItemCount, setAddLineItemCount] = useState(1);
  const [addLineItemCampaignIndex, setAddLineItemCampaignIndex] = useState(null);
  const [isAddingLineItems, setIsAddingLineItems] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [importCampaignIndex, setImportCampaignIndex] = useState(null);
  const [syncingCampaignIndex, setSyncingCampaignIndex] = useState(null); // Track which campaign is syncing
  const apolloClient = useApolloClient();

  const {
    form,
    formValues,
    derivedState,
    validationState,
    helpers
  } = useCampaignForm(mode);

  const {
    selectedAdvertiser,
    setSelectedAdvertiser,
    childrenOrg,
    fetchCampaignOrder,
    currentCampaignOrder,
    loadingCampaignOrder,
    currentUserOrg,
    fetchSelectedAdvertiser,
    // create-opportunity.ts resolvers (provided by CampaignRequestorProvider)
    createSalesforceOpportunity,
    createSalesforceOpportunityLineItems,
    createSalesforceContact,
    createSalesforceOpportunityContactRole,
    updateSalesforceOpportunityFields,
    sendPandaDocFromOpportunity,
    fetchSalesforceAccountRecordType,
    fetchSalesforceAccountNameDirect,
    fetchSalesforceOpportunityProposalFilename,
    // display-processor.ts resolvers (provided by CampaignRequestorProvider)
    upsertCampaignOrderCRMData,
    upsertCampaignOrderAttachment,
  } = useCampaignRequestorContext();

  // Get single line item save function
  const { saveSingleLineItem } = useCampaignSaveManager();

  // Ref to skip form reinitialization after single line item saves
  const skipFormReInitRef = useRef(false);

  // Helper to get default CPM from advertiser or fallback to 7
  const getDefaultCpm = () => {
    if (selectedAdvertiser) {
      return getDefaultCpmHelper(selectedAdvertiser);
    }
    if (currentUserOrg) {
      return getDefaultCpmHelper(currentUserOrg);
    }
    return 7; // Fallback default
  };

  // Initialize form with campaign data when loading existing campaign
  useEffect(() => {
    if (id) {
      fetchCampaignOrder({
        variables: {
          where: { id },
        },
      });
    }
  }, [id, fetchCampaignOrder]);

  // Handle form initialization when campaign data is loaded
  useEffect(() => {
    if (currentCampaignOrder) {
      // Skip reinitialization if we just saved a single line item
      if (skipFormReInitRef.current) {
        skipFormReInitRef.current = false;
        return;
      }
      handleInitialValues(currentCampaignOrder, form, history, selectedAdvertiser);

      // Use advertiser data from campaign order directly instead of
      // querying org separately (which fails for cross-org campaigns
      // where child orgs can't access parent org data - see #10615)
      if (
        currentCampaignOrder.advertiser &&
        selectedAdvertiser &&
        currentCampaignOrder.advertiser.id !== selectedAdvertiser.id
      ) {
        setSelectedAdvertiser(currentCampaignOrder.advertiser);
      }
    } else if (!id) {
      // Initialize default values for new campaigns
      const moment = require('moment-timezone');
      // Normalize dates to start/end of day in user timezone, then convert to UTC
      const orderStartDate = moment.tz(timezone).startOf('day').utc();
      const orderEndDate = moment.tz(timezone).add(2, 'days').endOf('day').utc();

      form.setFieldsValue({
        startDate: orderStartDate,
        endDate: orderEndDate,
        // Pass order dates to campaigns so they inherit dates
        campaigns: CampaignConfig.getCampaignsDefaultValue(orderStartDate, orderEndDate),
      });

      // Set CPM from currentUserOrg when available
      if (currentUserOrg?.cpm) {
        const cpm = Number(currentUserOrg.cpm.replace(/,/g, ''));
        form.setFieldsValue({ cpm });
      }

      // Trigger initial validation to show required field errors
      setTimeout(() => {
        form.validateFields().catch(() => {
          // Ignore validation errors, we just want to show the error states
        });
      }, 100);
    }
  }, [currentCampaignOrder, currentUserOrg, form, history, fetchSelectedAdvertiser, selectedAdvertiser, id, setSelectedAdvertiser, timezone]);

  // Set initial CPM when currentUserOrg or selectedAdvertiser becomes available (for new campaigns)
  useEffect(() => {
    if (!id) {
      const currentCpm = form.getFieldValue('cpm');

      // Only set CPM if it's the default value (7) or not set
      if (!currentCpm || currentCpm === 7) {
        // Try to find org source from childrenOrg if selectedAdvertiser matches currentUserOrg
        let orgSource = null;

        if (childrenOrg && currentUserOrg) {
          const matchedOrg = childrenOrg.find(org => org.id === currentUserOrg.id);
          if (matchedOrg) {
            orgSource = matchedOrg;
          }
        }

        // Fallback to selectedAdvertiser or currentUserOrg
        if (!orgSource) {
          orgSource = selectedAdvertiser || currentUserOrg;
        }

        if (orgSource) {
          // Get default CPM for order level
          const orderCpm = getDefaultCpmHelper(orgSource);

          // Get current campaigns
          const campaigns = form.getFieldValue('campaigns');

          // Update CPM for campaign order, campaigns, and line items
          if (campaigns && campaigns.length > 0) {
            // Update each campaign with type-specific CPM
            const updatedCampaigns = campaigns.map((campaign, idx) => {
              const campaignCpm = getCpmForCampaignType(orgSource, campaign.type || 1);

              return {
                ...campaign,
                cpm: campaignCpm,
                lineItems: campaign.lineItems?.map((lineItem) => ({
                  ...lineItem,
                  cpm: campaignCpm, // Line items inherit campaign CPM
                })) || [],
              };
            });

            form.setFieldsValue({
              cpm: orderCpm,
              campaigns: updatedCampaigns,
            });
          } else {
            form.setFieldsValue({ cpm: orderCpm });
          }
        }
      }
    }
  }, [currentUserOrg, selectedAdvertiser, childrenOrg, id, form]);

  // Handle advertiser selection changes
  // Track if advertiser has changed to avoid resetting CPM on every render
  const prevSelectedAdvertiserRef = useRef(selectedAdvertiser?.id);

  useEffect(() => {
    if (selectedAdvertiser && childrenOrg && childrenOrg.length) {
      const matchedAdvertiser = childrenOrg.find((org) => org.id === selectedAdvertiser.id);

      if (matchedAdvertiser) {
        // Only update CPM if advertiser actually changed, not on every derivedState change
        const advertiserChanged = prevSelectedAdvertiserRef.current !== selectedAdvertiser.id;

        if (advertiserChanged) {
          // Order-level CPM
          const orderCpm = currentCampaignOrder
            ? currentCampaignOrder.cpm
            : getDefaultCpmHelper(matchedAdvertiser);

          helpers.setFieldsValue({ cpm: orderCpm });

          const campaigns = helpers.getFieldValue('campaigns');
          if (campaigns) {
            // Update each campaign with type-specific CPM and percentage fields
            const updatedCampaigns = campaigns.map((campaign, idx) => {
              // For existing orders, preserve CPM; for new, use type-specific
              const campaignCpm = currentCampaignOrder
                ? campaign.cpm
                : getCpmForCampaignType(matchedAdvertiser, campaign.type || 1);

              // Get pricing fields for the campaign
              const pricingFields = getPricingFieldsForSave(campaignCpm, matchedAdvertiser, campaign.type || 1);

              return {
                ...campaign,
                cpm: campaignCpm,
                // Always update percentage fields when advertiser changes (even for existing orders)
                advertiserPercentage: pricingFields.advertiserPercentage,
                contractPercentage: pricingFields.contractPercentage,
                contractCPM: pricingFields.contractCPM,
                impressions: derivedState.campaigns.find(c => c.id === campaign.id)?.impressions || campaign.impressions,
                lineItems: campaign.lineItems?.map((lineItem) => ({
                  ...lineItem,
                  cpm: campaignCpm, // Line items inherit campaign CPM
                })) || [],
              };
            });

            helpers.setFieldsValue({
              impressions: derivedState.impressions,
              campaigns: updatedCampaigns,
            });
          }

          setSelectedAdvertiser(matchedAdvertiser);
          prevSelectedAdvertiserRef.current = selectedAdvertiser.id;
        }
      }
    }
  }, [selectedAdvertiser, childrenOrg, currentCampaignOrder, setSelectedAdvertiser, helpers, derivedState]);

  // Advanced Mode Detection Logic
  useEffect(() => {
    const checkAdvancedMode = async () => {
      // Check if user has required permissions (isTrafficker OR isProcessor)
      const hasPermission = await isAuthorized(['isTrafficker', 'isProcessor'], []);
      // Check if campaign status is NOT DRAFT
      const campaignStatus = currentCampaignOrder?.status;
      const isNotDraft = campaignStatus && campaignStatus !== 'DRAFT';

      // Advanced Mode is available when user has permissions and status is not DRAFT
      // Note: "Has Media Plan Strategy" toggle is independent of Advanced Mode access
      const canAccessAdvancedMode = hasPermission && isNotDraft;

      // Set permission and status states for button visibility
      setHasTraffickerOrProcessorPermission(hasPermission);
      setIsNotDraftStatus(isNotDraft);
      setIsAdvancedMode(canAccessAdvancedMode);
    };

    checkAdvancedMode();
  }, [isAuthorized, formValues.campaigns, currentCampaignOrder]);

  // Handle adding multiple new line items (for modal)
  const handleAddLineItems = (count = 1) => {
    if (addLineItemCampaignIndex === null) return;

    setIsAddingLineItems(true);

    const campaign = formValues.campaigns?.[addLineItemCampaignIndex];
    const inheritedValueAdd = campaign?.valueAdd || false;
    const existingCount = campaign?.lineItems?.length || 0;
    const campaignCpm = campaign?.lineItems?.[0]?.cpm || 7;

    const newLineItems = [];
    for (let i = 0; i < count; i++) {
      const baseName = `Line Item ${existingCount + i + 1}`;
      newLineItems.push({
        name: inheritedValueAdd ? `${baseName}-$` : baseName,
        startDate: campaign?.startDate ? moment(campaign.startDate).utc().startOf('day') : moment().utc().startOf('day'),
        endDate: campaign?.endDate ? moment(campaign.endDate).utc().endOf('day') : moment().add(2, 'days').utc().endOf('day'),
        budgetTotal: 0,
        budgetPercentage: 0,
        cpm: campaignCpm,
        supplySidePlatform: 'Smart AdServer',
        maxBid: 4,
        frequencyCap: 4,
        regionGroups: [],
        targetingGroups: [],
        targetingType: null,
        targetingRegions: [],
        targetingLocations: [],
        targetingCountries: null,
        audienceType: null,
        firstPartyDataAudiences: [],
        adGroup: null,
        valueAdd: inheritedValueAdd,
      });
    }

    const currentCampaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = currentCampaigns.map((c, idx) => {
      if (idx === addLineItemCampaignIndex) {
        return { ...c, lineItems: [...(c.lineItems || []), ...newLineItems] };
      }
      return c;
    });
    form.setFieldsValue({ campaigns: updatedCampaigns });

    message.success(`${count} line item${count > 1 ? 's' : ''} created successfully`);
    setIsAddLineItemModalVisible(false);
    setAddLineItemCount(1);
    setAddLineItemCampaignIndex(null);
    setIsAddingLineItems(false);

    setTimeout(() => form.validateFields().catch(() => {}), 100);
  };

  // Handle importing line items from CSV/Excel file
  const handleImportLineItems = (lineItems, campaignIndex, options = {}) => {
    if (campaignIndex === null || campaignIndex === undefined) return;

    const currentCampaigns = form.getFieldValue('campaigns') || [];
    const campaignBudget = currentCampaigns[campaignIndex]?.budget || 0;

    // Normalize percentages: prefer CSV value, fallback to calculated
    let lineItemsWithPercentage = normalizeLineItemPercentages(lineItems, campaignBudget);

    // If copyFlightSchedules option is enabled, add flight schedules to each line item
    if (options.copyFlightSchedules && options.campaignFlightSchedules?.length) {
      lineItemsWithPercentage = lineItemsWithPercentage.map(lineItem => ({
        ...lineItem,
        flightSchedule: createFlightSchedulesWithBudget(
          options.campaignFlightSchedules,
          lineItem.budgetTotal,
          lineItem.cpm
        ),
      }));
    }

    const updatedCampaigns = currentCampaigns.map((c, idx) => {
      if (idx === campaignIndex) {
        return { ...c, lineItems: [...(c.lineItems || []), ...lineItemsWithPercentage] };
      }
      return c;
    });
    form.setFieldsValue({ campaigns: updatedCampaigns });

    setIsImportModalVisible(false);
    setImportCampaignIndex(null);

    setTimeout(() => form.validateFields().catch(() => {}), 100);
  };

  // Handle syncing line items from Nexxen DSP
  const handleSyncLineItems = async (campaignIndex) => {
    const adProviderId = form.getFieldValue(['campaigns', campaignIndex, 'adProviderId']);

    if (!adProviderId) {
      message.warning('No Nexxen Insertion Order ID (Ad Provider ID) found');
      return;
    }

    setSyncingCampaignIndex(campaignIndex);
    const hideLoading = message.loading('Syncing line items from Nexxen...', 0);

    try {
      const { data } = await apolloClient.query({
        query: GET_NEXXEN_LINE_ITEMS,
        variables: { insertionOrderId: adProviderId },
        fetchPolicy: 'network-only',
      });

      const nexxenLineItems = data?.nexxenLineItems || [];
      const currentLineItems = form.getFieldValue(['campaigns', campaignIndex, 'lineItems']) || [];

      // Find Nexxen line items not yet in dashboard
      const existingDspIds = new Set(
        currentLineItems.map(li => li.dspLineItemId).filter(Boolean)
      );
      const newNexxenLineItems = nexxenLineItems.filter(
        nli => !existingDspIds.has(nli.id)
      );

      if (newNexxenLineItems.length === 0) {
        hideLoading();
        setSyncingCampaignIndex(null);
        message.info('All line items are already synced');
        return;
      }

      // Get campaign CPM as default
      const campaignCpmValue = form.getFieldValue(['campaigns', campaignIndex, 'cpm']);

      // Get campaign dates as fallback
      const campaignStartDate = form.getFieldValue(['campaigns', campaignIndex, 'startDate']);
      const campaignEndDate = form.getFieldValue(['campaigns', campaignIndex, 'endDate']);

      // Transform Nexxen line items to dashboard format
      const transformedLineItems = newNexxenLineItems.map(nli => {
        const schedules = nli.budget?.schedules || [];
        const firstSchedule = schedules[0];
        const lastSchedule = schedules[schedules.length - 1];
        const totalBudget = schedules.reduce((sum, s) => sum + (s.budget || 0), 0);

        // Use Nexxen dates if available, otherwise fall back to campaign dates
        let startDate = firstSchedule?.startDate
          ? moment(firstSchedule.startDate)
          : (campaignStartDate ? moment(campaignStartDate) : moment());
        let endDate = lastSchedule?.endDate
          ? moment(lastSchedule.endDate)
          : (campaignEndDate ? moment(campaignEndDate) : moment().add(30, 'days'));

        // Ensure endDate is after startDate (add 1 day minimum if they're the same or invalid)
        if (endDate.isSameOrBefore(startDate)) {
          endDate = moment(startDate).add(1, 'day');
        }

        return {
          name: nli.name,
          dspLineItemId: nli.id,
          status: nli.status === 'play' ? 'PLAY' : 'PAUSE',
          startDate,
          endDate,
          budgetTotal: totalBudget,
          cpm: nli.bidding?.bids?.[0] || campaignCpmValue,
          isOrigin: false, // Synced from Nexxen, not client-created
          markedForDelivery: nli.status === 'play',
          valueAdd: false,
        };
      });

      // Append to existing line items
      form.setFieldValue(
        ['campaigns', campaignIndex, 'lineItems'],
        [...currentLineItems, ...transformedLineItems]
      );

      message.success(`Added ${transformedLineItems.length} new line item(s) from Nexxen`);
    } catch (error) {
      message.error(`Sync failed: ${error.message}`);
    } finally {
      hideLoading();
      setSyncingCampaignIndex(null);
    }
  };

  if (loadingCampaignOrder) {
    return <OverlaySpinner />;
  }

  return (
    <div style={{ margin: 20 }}>
      <Form
        form={form}
        onValuesChange={helpers.handleValuesChange}
        validateTrigger={['onChange', 'onBlur']}
        initialValues={{
          name: '',
          description: '',
          startDate: undefined,
          endDate: undefined,
          numberOfDays: 2,
          type: 1,
          renewalStatus: 'EXPECTED_TO_RENEW',
          advertiserId: selectedAdvertiser ? selectedAdvertiser.id : null,
          budget: 0,
          cpm: getDefaultCpm(),
          isRegulated: true,
          category: defaultIabCategory.code,
          attributionTargets: [],
          geoTargets: [],
          measureWalkInTraffic: false,
          advertiserType: 'LOCAL_DELIVERY_SERVICE',
          transactionTarget: null,
          transactionsConfig: null,
          signUpConfig: null,
          awarenessCampaign: false,
          sessionConfig: null,
          notes: null,
          IONumber: null,
          additionalIONumbers: [],
          productCode: 'DIS',
          complianceAudiences: [],
          campaigns: CampaignConfig.campaignsDefaultValue,
        }}
      >
        <Row gutter={20}>
          <Col span={6}>
            <CampaignRequestorSummary
              formValues={formValues}
              derivedState={derivedState}
              validationState={validationState}
              helpers={helpers}
              mode={mode}
              handleInitialValues={(campaignOrder) =>
                handleInitialValues(campaignOrder, form, history)
              }
              showOnlyDeliveryItems={showOnlyDeliveryItems}
              isAdvancedMode={isAdvancedMode}
            />
          </Col>
          <Col span={18}>
            <Tabs defaultActiveKey="form">
              <Tabs.TabPane tab="Form" key="form">
                <Row>
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {/* Advanced Mode Banner */}
                    {isAdvancedMode && (
                      <Alert
                        message={
                          <Space>
                            <ToolOutlined />
                            <span><strong>Advanced Mode</strong> - Ad Ops Configuration</span>
                          </Space>
                        }
                        description="You have access to advanced campaign configuration features including DSP settings and additional line item options."
                        type="success"
                        showIcon={false}
                        banner
                      />
                    )}

                    <CampaignRequestorDetails
                      formValues={formValues}
                      helpers={helpers}
                    />
                    <CampaignRequestorBudget
                      formValues={formValues}
                      derivedState={derivedState}
                      helpers={helpers}
                    />
                    <CampaignRequestorCategories
                      formValues={formValues}
                      helpers={helpers}
                    />

                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue, setFieldsValue }) => {
                        const isRegulated = getFieldValue('isRegulated');
                        const category = getFieldValue('category');
                        const advertiserType = getFieldValue('advertiserType');

                        // Handle category being either a string or an array
                        const isCannabisCategory = Array.isArray(category)
                          ? category.includes('IAB7-5')
                          : category === 'IAB7-5';

                        // Only show Compliance Targeting for Cannabis/CBD Retail stores
                        const showComplianceTargeting =
                          isRegulated === true &&
                          isCannabisCategory &&
                          advertiserType === 'RETAIL_LOCAL_BRICK_AND_MORTAR';

                        if (!showComplianceTargeting) return null;

                        return (
                          <CampaignRequestorComplianceAudiences
                            complianceAudiences={getFieldValue('complianceAudiences') || []}
                            advertiserId={selectedAdvertiser ? selectedAdvertiser.id : currentUserOrg?.id}
                            onAddAudiences={(audiences) => {
                              setFieldsValue({ complianceAudiences: audiences });
                            }}
                            onRemoveAudience={(audienceId) => {
                              const current = getFieldValue('complianceAudiences') || [];
                              setFieldsValue({
                                complianceAudiences: current.filter((a) => a.id !== audienceId),
                              });
                            }}
                          />
                        );
                      }}
                    </Form.Item>

                    <CampaignRequestorAttributionReporting
                      formValues={formValues}
                      helpers={helpers}
                      google={google}
                    />

                    {/* Budget Allocation Widget */}
                    <CampaignBudgetAllocation
                      campaigns={formValues.campaigns || []}
                      orderBudget={formValues.budget || 0}
                    />

                    {/* Multi-Campaign Support with Nested Line Items */}
                    <Form.List name="campaigns">
                      {(campaignFields, { add: addCampaign, remove: removeCampaign }) => {
                        const constraints = getCampaignCountConstraints();
                        const canAddMore = campaignFields.length < constraints.max;
                        const canDelete = campaignFields.length > constraints.min;

                        const handleAddCampaign = () => {
                          if (!canAddMore) {
                            message.warning(`Maximum ${constraints.max} campaigns allowed per order`);
                            return;
                          }

                          const moment = require('moment-timezone');
                          // Normalize dates to start/end of day in user timezone, then convert to UTC
                          const campaignStartDate = formValues.startDate
                            ? moment(formValues.startDate).tz(timezone).startOf('day').utc()
                            : moment.tz(timezone).startOf('day').utc();
                          const campaignEndDate = formValues.endDate
                            ? moment(formValues.endDate).tz(timezone).endOf('day').utc()
                            : moment.tz(timezone).add(2, 'days').endOf('day').utc();

                          // Get CPM for new campaign based on default type (DISPLAY = 1)
                          const orgSource = selectedAdvertiser || currentUserOrg;
                          const newCampaignType = 1; // DISPLAY
                          const campaignCpm = orgSource
                            ? getCpmForCampaignType(orgSource, newCampaignType)
                            : 7;

                          const newCampaign = {
                            ...CampaignConfig.campaignDefaultValue,
                            name: '',
                            startDate: campaignStartDate,
                            endDate: campaignEndDate,
                            cpm: campaignCpm,
                            lineItems: [
                              {
                                ...CampaignConfig.lineItemsDefaultValue[0],
                                startDate: campaignStartDate,
                                endDate: campaignEndDate,
                                cpm: campaignCpm,
                              },
                            ],
                          };

                          addCampaign(newCampaign);
                          message.success(`Campaign ${campaignFields.length + 1} added`);
                        };

                        const handleCloneCampaign = (index) => {
                          if (!canAddMore) {
                            message.warning(`Maximum ${constraints.max} campaigns allowed per order`);
                            return;
                          }

                          // Get the campaign to clone
                          const campaignToClone = formValues.campaigns[index];

                          if (!campaignToClone) {
                            message.error('Campaign not found');
                            return;
                          }

                          // Deep clone the campaign with all its line items
                          // Remove $ suffix from campaign name if it exists (value add campaigns have $)
                          const baseCampaignName = campaignToClone.name ? campaignToClone.name.replace(/-\$\s*$/, '').trim() : '';

                          const clonedCampaign = {
                            ...campaignToClone,
                            name: `${baseCampaignName} (Copy)`,
                            // Remove id to create a new campaign instead of updating existing
                            id: undefined,
                            // Remove DSP-specific fields (should not be cloned)
                            adProviderId: undefined,
                            // Cloned campaigns are NOT marked as value add
                            valueAdd: false,
                            // Reset campaign-level flight schedules on clone
                            flightSchedules: [],
                            flightSchedule: [],
                            // Clone line items and remove their IDs and DSP settings
                            lineItems: (campaignToClone.lineItems || []).map((lineItem) => {
                              // Remove $ suffix from line item name if it exists
                              const baseLineItemName = lineItem.name ? lineItem.name.replace(/-\$\s*$/, '').trim() : 'Line Item';
                              // Deep clone adGroup to remove DSP IDs from creatives and medias
                              let clonedAdGroup = lineItem.adGroup;
                              if (clonedAdGroup) {
                                clonedAdGroup = {
                                  ...clonedAdGroup,
                                  id: undefined,
                                  adGroupCreatives: (clonedAdGroup.adGroupCreatives || []).map((creative) => ({
                                    ...creative,
                                    id: undefined,
                                    adProviderId: undefined, // Remove DSP creative ID
                                    medias: (creative.medias || []).map((media) => ({
                                      ...media,
                                      id: undefined,
                                      dspCreativeId: undefined, // Remove DSP creative ID
                                      dspAdId: undefined, // Remove DSP ad ID
                                    })),
                                  })),
                                };
                              }
                              return {
                                ...lineItem,
                                id: undefined, // Remove ID to create new line item
                                name: `${baseLineItemName} (Copy)`,
                                // Remove DSP-specific fields (should not be cloned)
                                dspLineItemId: undefined,
                                adProviderId: undefined,
                                // Cloned line items are NOT origin (client-created)
                                isOrigin: false,
                                // Cloned line items are NOT marked as value add
                                valueAdd: false,
                                // Use the cleaned adGroup
                                adGroup: clonedAdGroup,
                              };
                            }),
                          };

                          // Add the cloned campaign
                          addCampaign(clonedCampaign);
                          message.success(`Campaign cloned successfully as "${clonedCampaign.name}"`);
                        };

                        const handleDeleteCampaign = (index) => {
                          if (!canDelete) {
                            message.warning('Cannot delete the last campaign');
                            return;
                          }

                          removeCampaign(index);
                        };

                        return (
                          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            {/* Render each campaign */}
                            {campaignFields.map(({ key: campaignKey, name: campaignName, fieldKey: campaignFieldKey }, campaignIndex) => (
                              <Card
                                key={campaignKey}
                                id={`requester-campaign-${campaignIndex}`}
                                style={{
                                  border: '2px solid #1890ff',
                                  borderRadius: '8px',
                                  backgroundColor: '#fafafa',
                                }}
                              >
                                {/* Campaign Header with campaign-level fields */}
                                <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => {
                                  const prev = prevValues.campaigns?.[campaignIndex];
                                  const curr = currentValues.campaigns?.[campaignIndex];
                                  return prev !== curr ||
                                    prevValues.budget !== currentValues.budget ||
                                    prevValues.startDate !== currentValues.startDate ||
                                    prevValues.endDate !== currentValues.endDate;
                                }}>
                                  {({ getFieldValue }) => (
                                    <CampaignHeader
                                      campaignIndex={campaignIndex}
                                      form={form}
                                      orderBudget={formValues.budget || 0}
                                      orderDates={{
                                        startDate: formValues.startDate,
                                        endDate: formValues.endDate,
                                      }}
                                      onClone={handleCloneCampaign}
                                      onDelete={handleDeleteCampaign}
                                      campaignCount={campaignFields.length}
                                      campaigns={formValues.campaigns || []}
                                      isAdvancedMode={isAdvancedMode}
                                      helpers={helpers}
                                      hasTraffickerOrProcessorPermission={hasTraffickerOrProcessorPermission}
                                      isNotDraftStatus={isNotDraftStatus}
                                    />
                                  )}
                                </Form.Item>

                                {/* Line Items within this campaign */}
                                <Form.List name={[campaignName, 'lineItems']}>
                                  {(lineItemFields, { add: addLineItem, remove: removeLineItem }) => {
                                    // Bulk clone state management - per campaign
                                    const campaignKey = `campaign-${campaignIndex}`;
                                    const currentCampaignSelections = selectedLineItemIndices[campaignKey] || [];
                                    const currentLineItems = formValues.campaigns?.[campaignIndex]?.lineItems || [];
                                    const totalLineItems = lineItemFields.length;

                                    // Bulk clone handlers
                                    const handleLineItemSelectionChange = (lineItemIndex, checked) => {
                                      setSelectedLineItemIndices(prev => {
                                        const campaignSelections = prev[campaignKey] || [];
                                        const updated = checked
                                          ? [...campaignSelections, lineItemIndex]
                                          : campaignSelections.filter(idx => idx !== lineItemIndex);
                                        return { ...prev, [campaignKey]: updated };
                                      });
                                    };

                                    const handleSelectAll = (e) => {
                                      if (e.target.checked) {
                                        const allIndices = lineItemFields
                                          .map((field, idx) => idx)
                                          .filter(idx => {
                                            if (showOnlyDeliveryItems) {
                                              const lineItem = currentLineItems[idx];
                                              return lineItem && lineItem.markedForDelivery === true;
                                            }
                                            return true;
                                          });
                                        setSelectedLineItemIndices(prev => ({ ...prev, [campaignKey]: allIndices }));
                                      } else {
                                        setSelectedLineItemIndices(prev => ({ ...prev, [campaignKey]: [] }));
                                      }
                                    };

                                    const handleDeselectAll = () => {
                                      setSelectedLineItemIndices(prev => ({ ...prev, [campaignKey]: [] }));
                                    };

                                    const handleBulkClone = () => {
                                      if (currentCampaignSelections.length === 0) {
                                        message.warning('Please select line items to clone');
                                        return;
                                      }

                                      const moment = require('moment-timezone');

                                      const clonedItems = currentCampaignSelections.map(selectedIndex => {
                                        const sourceItem = JSON.parse(JSON.stringify(currentLineItems[selectedIndex]));

                                        let newLineItem = {
                                          ...sourceItem,
                                          budgetTotal: 0,
                                          budgetPercentage: 0,
                                          impressions: 0,
                                          name: `${sourceItem.name} (Copy)`,
                                          id: undefined,
                                        };

                                        // Convert dates back to Moment objects (JSON.stringify converts them to ISO strings)
                                        if (newLineItem.startDate) {
                                          newLineItem.startDate = moment.isMoment(newLineItem.startDate)
                                            ? newLineItem.startDate
                                            : moment(newLineItem.startDate);
                                        }
                                        if (newLineItem.endDate) {
                                          newLineItem.endDate = moment.isMoment(newLineItem.endDate)
                                            ? newLineItem.endDate
                                            : moment(newLineItem.endDate);
                                        }

                                        // Cloned items are NOT origin (client-created) or value add
                                        newLineItem.isOrigin = false;
                                        newLineItem.valueAdd = false;

                                        // Set markedForDelivery based on advanced mode
                                        if (isAdvancedMode) {
                                          // In advanced mode, automatically mark cloned items for delivery
                                          newLineItem.markedForDelivery = true;
                                        } else {
                                          // In normal mode, exclude markedForDelivery
                                          delete newLineItem.markedForDelivery;
                                        }

                                        if (newLineItem.adGroup && newLineItem.adGroup.id) {
                                          delete newLineItem.adGroup.id;
                                          if (newLineItem.adGroup.adGroupCreatives) {
                                            newLineItem.adGroup.adGroupCreatives = newLineItem.adGroup.adGroupCreatives.map(creative => {
                                              delete creative.id;

                                              // Convert creative dates back to Moment objects
                                              if (creative.startDate) {
                                                creative.startDate = moment.isMoment(creative.startDate)
                                                  ? creative.startDate
                                                  : moment(creative.startDate);
                                              }
                                              if (creative.endDate) {
                                                creative.endDate = moment.isMoment(creative.endDate)
                                                  ? creative.endDate
                                                  : moment(creative.endDate);
                                              }

                                              if (creative.medias) {
                                                creative.medias = creative.medias.map(media => {
                                                  delete media.id;
                                                  return media;
                                                });
                                              }
                                              return creative;
                                            });
                                          }
                                        }

                                        if (newLineItem.firstPartyDataAudiences) {
                                          newLineItem.firstPartyDataAudiences = newLineItem.firstPartyDataAudiences.map(audience => {
                                            delete audience.id;
                                            return audience;
                                          });
                                        }

                                        if (newLineItem.targetingRegions) {
                                          newLineItem.targetingRegions = newLineItem.targetingRegions.map(region => {
                                            delete region.id;
                                            return region;
                                          });
                                        }

                                        if (newLineItem.targetingLocations) {
                                          newLineItem.targetingLocations = newLineItem.targetingLocations.map(location => {
                                            delete location.id;
                                            return location;
                                          });
                                        }

                                        // Handle flight schedules: Always start with empty array when cloning
                                        newLineItem.flightSchedule = [];
                                        delete newLineItem.flightSchedules;

                                        // Rebuild the entire object with moment conversions (matching single clone logic)
                                        newLineItem = {
                                          ...newLineItem,
                                          startDate: newLineItem.startDate ? moment(newLineItem.startDate) : null,
                                          endDate: newLineItem.endDate ? moment(newLineItem.endDate) : null,
                                          flightSchedule: newLineItem.flightSchedule
                                            ? newLineItem.flightSchedule.map((flight) => ({
                                                ...flight,
                                                startDate: flight.startDate ? moment(flight.startDate) : null,
                                                endDate: flight.endDate ? moment(flight.endDate) : null,
                                              }))
                                            : [],
                                          adGroup:
                                            newLineItem.adGroup && newLineItem.adGroup.adGroupCreatives
                                              ? {
                                                  ...newLineItem.adGroup,
                                                  adGroupCreatives: newLineItem.adGroup.adGroupCreatives.map((adGroupCreative) => {
                                                    return {
                                                      ...adGroupCreative,
                                                      startDate: adGroupCreative.startDate ? moment(adGroupCreative.startDate) : null,
                                                      endDate: adGroupCreative.endDate ? moment(adGroupCreative.endDate) : null,
                                                    };
                                                  }),
                                                }
                                              : null,
                                        };

                                        return newLineItem;
                                      });

                                      const updatedLineItems = [...currentLineItems, ...clonedItems];
                                      const currentCampaigns = form.getFieldValue('campaigns') || [];
                                      const updatedCampaigns = [...currentCampaigns];
                                      updatedCampaigns[campaignIndex] = {
                                        ...updatedCampaigns[campaignIndex],
                                        lineItems: updatedLineItems,
                                      };
                                      form.setFieldsValue({ campaigns: updatedCampaigns });

                                      message.success(`Successfully cloned ${clonedItems.length} line item(s)`);
                                      setSelectedLineItemIndices(prev => ({ ...prev, [campaignKey]: [] }));
                                      setIsBulkCloneMode(false);

                                      setTimeout(() => {
                                        form.validateFields().catch(() => {});
                                      }, 100);
                                    };

                                    // Handle bulk delete multiple line items
                                    const handleBulkDelete = () => {
                                      // Filter out index 0 and isOrigin items (in advanced mode)
                                      const deletableIndices = currentCampaignSelections.filter(index => {
                                        if (index === 0) return false;
                                        if (isAdvancedMode && currentLineItems[index]?.isOrigin) return false;
                                        return true;
                                      });

                                      if (deletableIndices.length === 0) {
                                        message.warning('No deletable items selected. The first line item and origin items cannot be deleted.');
                                        return;
                                      }

                                      const updatedLineItems = currentLineItems.filter((_, idx) => !deletableIndices.includes(idx));
                                      const currentCampaigns = form.getFieldValue('campaigns') || [];
                                      const updatedCampaigns = [...currentCampaigns];
                                      updatedCampaigns[campaignIndex] = {
                                        ...updatedCampaigns[campaignIndex],
                                        lineItems: updatedLineItems,
                                      };
                                      form.setFieldsValue({ campaigns: updatedCampaigns });

                                      message.success(`${deletableIndices.length} line item${deletableIndices.length > 1 ? 's' : ''} deleted`);
                                      setSelectedLineItemIndices(prev => ({ ...prev, [campaignKey]: [] }));
                                      setIsBulkCloneMode(false);

                                      setTimeout(() => {
                                        form.validateFields().catch(() => {});
                                      }, 100);
                                    };

                                    // Handle bulk mark for delivery
                                    const handleBulkMarkForDelivery = () => {
                                      // Validation function for line items
                                      const validateLineItemForDelivery = (lineItem) => {
                                        const missingFields = [];
                                        if (!lineItem.name || lineItem.name.trim() === '') missingFields.push('Line Item Name');
                                        if (!lineItem.startDate) missingFields.push('Start Date');
                                        if (!lineItem.endDate) missingFields.push('End Date');
                                        const budget = parseFloat(lineItem.budgetTotal);
                                        if (!budget || budget <= 0) missingFields.push('Budget');
                                        if (!lineItem.targetingType) missingFields.push('Targeting Type');
                                        // CPM validation would depend on pricing model, simplified here
                                        const cpm = parseFloat(lineItem.cpm);
                                        if (!cpm || cpm < 1) missingFields.push('CPM');
                                        return { isValid: missingFields.length === 0, missingFields };
                                      };

                                      // Get valid items that can be marked
                                      const validIndices = currentCampaignSelections.filter(index => {
                                        const validation = validateLineItemForDelivery(currentLineItems[index]);
                                        return validation.isValid;
                                      });

                                      if (validIndices.length === 0) {
                                        message.warning('No valid items to mark. Selected items are missing required fields.');
                                        return;
                                      }

                                      // Check if all valid selected items are already marked - if so, unmark them
                                      const allMarked = validIndices.every(index => currentLineItems[index]?.markedForDelivery);
                                      const newStatus = !allMarked;

                                      const currentCampaigns = form.getFieldValue('campaigns') || [];
                                      const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
                                        if (idx === campaignIndex) {
                                          return {
                                            ...campaign,
                                            lineItems: campaign.lineItems.map((li, i) => {
                                              if (validIndices.includes(i)) {
                                                return { ...li, markedForDelivery: newStatus };
                                              }
                                              return li;
                                            }),
                                          };
                                        }
                                        return campaign;
                                      });
                                      form.setFieldsValue({ campaigns: updatedCampaigns });

                                      const action = newStatus ? 'marked for delivery' : 'unmarked from delivery';
                                      message.success(`${validIndices.length} line item${validIndices.length > 1 ? 's' : ''} ${action}`);
                                      setSelectedLineItemIndices(prev => ({ ...prev, [campaignKey]: [] }));
                                    };

                                    // Handle bulk value add toggle
                                    const handleBulkValueAdd = () => {
                                      const campaignValueAdd = formValues.campaigns?.[campaignIndex]?.valueAdd;

                                      if (campaignValueAdd) {
                                        message.warning('Value Add is controlled at the campaign level and cannot be changed individually.');
                                        return;
                                      }

                                      // Filter out origin items
                                      const toggleableIndices = currentCampaignSelections.filter(index => !currentLineItems[index]?.isOrigin);

                                      if (toggleableIndices.length === 0) {
                                        message.warning('No toggleable items selected. Origin items cannot be modified.');
                                        return;
                                      }

                                      // Check if all toggleable selected items are already value add - if so, remove it
                                      const allValueAdd = toggleableIndices.every(index => currentLineItems[index]?.valueAdd);
                                      const newStatus = !allValueAdd;

                                      const currentCampaigns = form.getFieldValue('campaigns') || [];
                                      const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
                                        if (idx === campaignIndex) {
                                          return {
                                            ...campaign,
                                            lineItems: campaign.lineItems.map((li, i) => {
                                              if (toggleableIndices.includes(i)) {
                                                let updatedName = li.name?.trim() || '';
                                                if (newStatus) {
                                                  if (!updatedName.endsWith('-$')) {
                                                    updatedName = updatedName + '-$';
                                                  }
                                                } else {
                                                  if (updatedName.endsWith('-$')) {
                                                    updatedName = updatedName.slice(0, -2).trim();
                                                  }
                                                }
                                                return { ...li, valueAdd: newStatus, name: updatedName };
                                              }
                                              return li;
                                            }),
                                          };
                                        }
                                        return campaign;
                                      });
                                      form.setFieldsValue({ campaigns: updatedCampaigns });

                                      const action = newStatus ? 'marked as Value Add' : 'unmarked from Value Add';
                                      message.success(`${toggleableIndices.length} line item${toggleableIndices.length > 1 ? 's' : ''} ${action}`);
                                      setSelectedLineItemIndices(prev => ({ ...prev, [campaignKey]: [] }));
                                    };

                                    // Check if table view is active for this campaign
                                    const isTableView = lineItemViewModes[campaignIndex] === 'table' && hasTraffickerOrProcessorPermission;

                                    // Render Table View
                                    if (isTableView) {
                                      return (
                                        <CampaignRequestorLineItemTable
                                          campaignIndex={campaignIndex}
                                          formValues={formValues}
                                          derivedState={derivedState}
                                          helpers={helpers}
                                          lineItemFields={lineItemFields}
                                          addLineItem={addLineItem}
                                          removeLineItem={removeLineItem}
                                          currentCampaignOrder={currentCampaignOrder}
                                          isAdvancedMode={isAdvancedMode}
                                          hasTraffickerOrProcessorPermission={hasTraffickerOrProcessorPermission}
                                          isNotDraftStatus={isNotDraftStatus}
                                          campaignDsp={formValues.campaigns?.[campaignIndex]?.demandSidePlatform}
                                          campaignValueAdd={formValues.campaigns?.[campaignIndex]?.valueAdd}
                                          form={form}
                                          viewMode={lineItemViewModes[campaignIndex] || 'table'}
                                          onViewModeChange={(mode) => setLineItemViewModes(prev => ({ ...prev, [campaignIndex]: mode }))}
                                          showOnlyDeliveryItems={showOnlyDeliveryItems}
                                          onDeliveryFilterChange={setShowOnlyDeliveryItems}
                                          onSyncLineItems={formValues.campaigns?.[campaignIndex]?.adProviderId ? () => handleSyncLineItems(campaignIndex) : null}
                                          isSyncing={syncingCampaignIndex === campaignIndex}
                                          onSaveLineItem={async (lineItemData, lineItemIndex) => {
                                            const campaign = formValues.campaigns?.[campaignIndex];
                                            const campaignId = campaign?.id;
                                            const campaignOrderId = currentCampaignOrder?.id;

                                            if (!campaignOrderId || !campaignId) {
                                              message.error('Please save the campaign first before saving individual line items');
                                              return { success: false };
                                            }

                                            // Set flag to skip form reinitialization after save
                                            skipFormReInitRef.current = true;

                                            const result = await saveSingleLineItem({
                                              campaignOrderId,
                                              campaignId,
                                              lineItem: lineItemData,
                                              selectedAdvertiser,
                                              originalLineItems: currentCampaignOrder?.campaigns?.[campaignIndex]?.lineItems || [],
                                              campaignOrderStatus: currentCampaignOrder?.status,
                                            });

                                            // If save was successful and this was a new line item, update form state with new ID
                                            if (result.success && result.isCreateContext && result.savedLineItem) {
                                              const currentCampaigns = form.getFieldValue('campaigns') || [];
                                              const updatedCampaigns = currentCampaigns.map((c, cIdx) => {
                                                if (cIdx !== campaignIndex) return c;
                                                const updatedLineItems = (c.lineItems || []).map((li, liIdx) => {
                                                  if (liIdx === lineItemIndex) {
                                                    // Assign the new ID to this line item
                                                    return { ...li, id: result.savedLineItem.id };
                                                  }
                                                  return li;
                                                });
                                                return { ...c, lineItems: updatedLineItems };
                                              });
                                              form.setFieldsValue({ campaigns: updatedCampaigns });
                                            }

                                            return result;
                                          }}
                                        />
                                      );
                                    }

                                    // Render Card View (existing behavior)
                                    return (
                                      <Space direction="vertical" size="middle" style={{ width: '100%', paddingBottom: isBulkCloneMode ? '100px' : '0' }}>
                                        {/* Line Item Action Buttons */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <Space>
                                            <Button
                                              type="primary"
                                              icon={<PlusOutlined />}
                                              onClick={() => {
                                                // Add single line item directly
                                                const campaign = formValues.campaigns?.[campaignIndex];
                                                const inheritedValueAdd = campaign?.valueAdd || false;
                                                const existingCount = campaign?.lineItems?.length || 0;
                                                const campaignCpm = campaign?.lineItems?.[0]?.cpm || 7;
                                                const baseName = `Line Item ${existingCount + 1}`;

                                                const newLineItem = {
                                                  name: inheritedValueAdd ? `${baseName}-$` : baseName,
                                                  startDate: campaign?.startDate ? moment(campaign.startDate).utc().startOf('day') : moment().utc().startOf('day'),
                                                  endDate: campaign?.endDate ? moment(campaign.endDate).utc().endOf('day') : moment().add(2, 'days').utc().endOf('day'),
                                                  budgetTotal: 0,
                                                  budgetPercentage: 0,
                                                  cpm: campaignCpm,
                                                  regionGroups: [],
                                                  targetingGroups: [],
                                                  targetingType: null,
                                                  targetingRegions: [],
                                                  targetingLocations: [],
                                                  targetingCountries: null,
                                                  audienceType: null,
                                                  firstPartyDataAudiences: [],
                                                  adGroup: null,
                                                  valueAdd: inheritedValueAdd,
                                                };

                                                addLineItem(newLineItem);
                                                message.success('Line item created successfully');
                                              }}
                                            >
                                              Add Line Item
                                            </Button>
                                            <Dropdown
                                              menu={{
                                                items: [
                                                  {
                                                    key: 'add-multiple',
                                                    icon: <PlusOutlined />,
                                                    label: 'Add Multiple',
                                                    onClick: () => {
                                                      setAddLineItemCampaignIndex(campaignIndex);
                                                      setIsAddLineItemModalVisible(true);
                                                    },
                                                  },
                                                  ...(hasTraffickerOrProcessorPermission ? [{
                                                    key: 'import',
                                                    icon: <ImportOutlined />,
                                                    label: 'Import',
                                                    onClick: () => {
                                                      setImportCampaignIndex(campaignIndex);
                                                      setIsImportModalVisible(true);
                                                    },
                                                  }] : []),
                                                  {
                                                    key: 'multi-select',
                                                    icon: <CopyOutlined />,
                                                    label: isBulkCloneMode ? 'Cancel Multi-Select' : 'Multi-Select',
                                                    onClick: () => setIsBulkCloneMode(!isBulkCloneMode),
                                                  },
                                                  // Sync from Nexxen - only show for NEXXEN DSP campaigns with Ad Provider ID
                                                  ...(formValues.campaigns?.[campaignIndex]?.demandSidePlatform === 'NEXXEN' &&
                                                      formValues.campaigns?.[campaignIndex]?.adProviderId ? [
                                                    { type: 'divider' },
                                                    {
                                                      key: 'sync-nexxen',
                                                      icon: <SyncOutlined spin={syncingCampaignIndex === campaignIndex} />,
                                                      label: 'Sync from Nexxen',
                                                      disabled: syncingCampaignIndex === campaignIndex,
                                                      onClick: () => handleSyncLineItems(campaignIndex),
                                                    },
                                                  ] : []),
                                                  ...(hasTraffickerOrProcessorPermission && isNotDraftStatus ? [
                                                    { type: 'divider' },
                                                    {
                                                      key: 'delivery-only',
                                                      icon: <FilterOutlined />,
                                                      label: (
                                                        <Space>
                                                          <span>Delivery Only</span>
                                                          <Switch
                                                            size="small"
                                                            checked={showOnlyDeliveryItems}
                                                            onChange={(checked, e) => {
                                                              e.stopPropagation();
                                                              setShowOnlyDeliveryItems(checked);
                                                            }}
                                                          />
                                                        </Space>
                                                      ),
                                                    },
                                                  ] : []),
                                                ],
                                              }}
                                              trigger={['click']}
                                            >
                                              <Button icon={<MoreOutlined />} />
                                            </Dropdown>
                                          </Space>
                                          {/* View Mode Toggle */}
                                          {hasTraffickerOrProcessorPermission && (
                                            <Segmented
                                              size="small"
                                              value={lineItemViewModes[campaignIndex] || 'card'}
                                              onChange={(mode) => setLineItemViewModes(prev => ({ ...prev, [campaignIndex]: mode }))}
                                              options={[
                                                { label: 'Cards', value: 'card', icon: <AppstoreOutlined /> },
                                                { label: 'Table', value: 'table', icon: <TableOutlined /> },
                                              ]}
                                            />
                                          )}
                                          {/* Expand/Collapse All - only for card view */}
                                          {(lineItemViewModes[campaignIndex] || 'card') === 'card' && lineItemFields.length > 1 && (
                                            <Space size="small">
                                              <Tooltip title="Expand All">
                                                <Button
                                                  size="small"
                                                  icon={<ExpandAltOutlined />}
                                                  onClick={() => {
                                                    const allIndices = lineItemFields.map((_, idx) => idx);
                                                    setExpandedLineItems(prev => ({ ...prev, [campaignIndex]: allIndices }));
                                                  }}
                                                />
                                              </Tooltip>
                                              <Tooltip title="Collapse All">
                                                <Button
                                                  size="small"
                                                  icon={<ShrinkOutlined />}
                                                  onClick={() => {
                                                    setExpandedLineItems(prev => ({ ...prev, [campaignIndex]: [] }));
                                                  }}
                                                />
                                              </Tooltip>
                                            </Space>
                                          )}
                                        </div>

                                        {lineItemFields.map(({ key: lineItemKey, name: lineItemName, fieldKey: lineItemFieldKey, ...restField }, lineItemIndex) => {
                                        // Calculate the flat index for backwards compatibility
                                        const flatIndex = formValues.campaigns?.slice(0, campaignIndex).reduce(
                                          (sum, campaign) => sum + (campaign.lineItems?.length || 0),
                                          0
                                        ) + lineItemIndex;

                                        return (
                                          <CampaignRequestorLineItem
                                            key={lineItemKey}
                                            campaignIndex={campaignIndex}
                                            formValues={{
                                              ...formValues,
                                              // Map campaign-specific line items to top-level for compatibility
                                              campaigns: formValues.campaigns?.[campaignIndex]?.lineItems || [],
                                              startDate: formValues.campaigns?.[campaignIndex]?.startDate || formValues.startDate,
                                              endDate: formValues.campaigns?.[campaignIndex]?.endDate || formValues.endDate,
                                              // CRITICAL: Pass campaign-specific type, not order-level type
                                              type: formValues.campaigns?.[campaignIndex]?.type || formValues.type,
                                              // Pass campaign budget for percentage calculation
                                              campaignBudget: formValues.campaigns?.[campaignIndex]?.budget || 0,
                                            }}
                                            derivedState={derivedState}
                                            helpers={{
                                              ...helpers,
                                              // Override helpers to work with nested path
                                              getFieldValue: (path) => {
                                                if (Array.isArray(path) && path[0] === 'campaigns') {
                                                  return form.getFieldValue(['campaigns', campaignIndex, 'lineItems', lineItemIndex, ...path.slice(2)]);
                                                }
                                                return form.getFieldValue(path);
                                              },
                                              setFieldsValue: (values) => {
                                                if (values.campaigns) {
                                                  // The line item component passes the updated line items array as 'campaigns'
                                                  // We need to map this to the correct nested structure
                                                  const updatedLineItems = values.campaigns;

                                                  // Get current campaigns array
                                                  const currentCampaigns = form.getFieldValue('campaigns') || [];
                                                  const updatedCampaigns = [...currentCampaigns];

                                                  // Update the line items for the specific campaign
                                                  if (updatedCampaigns[campaignIndex]) {
                                                    updatedCampaigns[campaignIndex] = {
                                                      ...updatedCampaigns[campaignIndex],
                                                      lineItems: updatedLineItems,
                                                    };

                                                    form.setFieldsValue({ campaigns: updatedCampaigns });
                                                  }
                                                } else {
                                                  form.setFieldsValue(values);
                                                }
                                              },
                                            }}
                                            restField={restField}
                                            name={lineItemName}
                                            fieldKey={lineItemFieldKey}
                                            index={lineItemIndex}
                                            add={addLineItem}
                                            remove={removeLineItem}
                                            currentCampaignOrder={currentCampaignOrder}
                                            isAdvancedMode={isAdvancedMode}
                                            campaignDsp={formValues.campaigns?.[campaignIndex]?.demandSidePlatform}
                                            campaignValueAdd={formValues.campaigns?.[campaignIndex]?.valueAdd}
                                            showOnlyDeliveryItems={showOnlyDeliveryItems}
                                            isBulkCloneMode={isBulkCloneMode}
                                            isSelected={currentCampaignSelections.includes(lineItemIndex)}
                                            onSelectionChange={handleLineItemSelectionChange}
                                            hasTraffickerOrProcessorPermission={hasTraffickerOrProcessorPermission}
                                            isNotDraftStatus={isNotDraftStatus}
                                            isExpanded={expandedLineItems[campaignIndex]?.includes(lineItemIndex) ?? lineItemIndex === 0}
                                            onExpandChange={(expanded) => {
                                              setExpandedLineItems(prev => {
                                                const current = prev[campaignIndex] || [0]; // Default first item expanded
                                                if (expanded) {
                                                  return { ...prev, [campaignIndex]: [...current, lineItemIndex] };
                                                } else {
                                                  return { ...prev, [campaignIndex]: current.filter(i => i !== lineItemIndex) };
                                                }
                                              });
                                            }}
                                            onSaveLineItem={async (lineItemData, lineItemIndex) => {
                                              const campaign = formValues.campaigns?.[campaignIndex];
                                              const campaignId = campaign?.id;
                                              const campaignOrderId = currentCampaignOrder?.id;

                                              if (!campaignOrderId || !campaignId) {
                                                message.error('Please save the campaign first before saving individual line items');
                                                return { success: false };
                                              }

                                              // Set flag to skip form reinitialization after save
                                              skipFormReInitRef.current = true;

                                              const result = await saveSingleLineItem({
                                                campaignOrderId,
                                                campaignId,
                                                lineItem: lineItemData,
                                                selectedAdvertiser,
                                                originalLineItems: currentCampaignOrder?.campaigns?.[campaignIndex]?.lineItems || [],
                                                campaignOrderStatus: currentCampaignOrder?.status,
                                              });

                                              // If save was successful and this was a new line item, update form state with new ID
                                              if (result.success && result.isCreateContext && result.savedLineItem) {
                                                const currentCampaigns = form.getFieldValue('campaigns') || [];
                                                const updatedCampaigns = currentCampaigns.map((c, cIdx) => {
                                                  if (cIdx !== campaignIndex) return c;
                                                  const updatedLineItems = (c.lineItems || []).map((li, liIdx) => {
                                                    if (liIdx === lineItemIndex) {
                                                      // Assign the new ID to this line item
                                                      return { ...li, id: result.savedLineItem.id };
                                                    }
                                                    return li;
                                                  });
                                                  return { ...c, lineItems: updatedLineItems };
                                                });
                                                form.setFieldsValue({ campaigns: updatedCampaigns });
                                              }

                                              return result;
                                            }}
                                          />
                                        );
                                      })}

                                        {/* Floating Action Bar */}
                                        {isBulkCloneMode && (
                                          <div
                                            style={{
                                              position: 'fixed',
                                              bottom: 0,
                                              left: 0,
                                              right: 0,
                                              backgroundColor: '#fff',
                                              borderTop: '1px solid #e8e8e8',
                                              padding: '16px 24px',
                                              boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
                                              zIndex: 1000,
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                            }}
                                          >
                                            <Space size="large">
                                              <Checkbox
                                                checked={currentCampaignSelections.length === totalLineItems && totalLineItems > 0}
                                                indeterminate={currentCampaignSelections.length > 0 && currentCampaignSelections.length < totalLineItems}
                                                onChange={handleSelectAll}
                                              >
                                                Select All
                                              </Checkbox>
                                              <Text strong>
                                                {currentCampaignSelections.length} of {totalLineItems} item(s) selected
                                              </Text>
                                            </Space>

                                            <Space>
                                              <Button onClick={handleDeselectAll}>
                                                Deselect All
                                              </Button>
                                              <Button
                                                onClick={() => {
                                                  setIsBulkCloneMode(false);
                                                  setSelectedLineItemIndices(prev => ({ ...prev, [campaignKey]: [] }));
                                                }}
                                              >
                                                Cancel
                                              </Button>
                                              <Popconfirm
                                                title="Delete selected line items?"
                                                description={`This will delete ${currentCampaignSelections.filter(i => i !== 0 && !(isAdvancedMode && currentLineItems[i]?.isOrigin)).length} item(s). The first line item cannot be deleted.`}
                                                onConfirm={handleBulkDelete}
                                                okText="Delete"
                                                okButtonProps={{ danger: true }}
                                                cancelText="Cancel"
                                              >
                                                <Tooltip title={`Delete Selected (${currentCampaignSelections.filter(i => i !== 0 && !(isAdvancedMode && currentLineItems[i]?.isOrigin)).length})`}>
                                                  <Button
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    disabled={currentCampaignSelections.length === 0}
                                                  />
                                                </Tooltip>
                                              </Popconfirm>
                                              {/* Bulk Mark for Delivery - only visible when user has permissions and status is not DRAFT */}
                                              {hasTraffickerOrProcessorPermission && isNotDraftStatus && (
                                                <Tooltip title="Mark for Delivery">
                                                  <Button
                                                    icon={<RocketOutlined />}
                                                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
                                                    onClick={handleBulkMarkForDelivery}
                                                    disabled={currentCampaignSelections.length === 0}
                                                  />
                                                </Tooltip>
                                              )}
                                              {/* Bulk Value Add - only visible when user has permissions and status is not DRAFT */}
                                              {hasTraffickerOrProcessorPermission && isNotDraftStatus && (
                                                <Tooltip title={formValues.campaigns?.[campaignIndex]?.valueAdd ? 'Value Add controlled at campaign level' : 'Toggle Value Add'}>
                                                  <Button
                                                    icon={<GiftOutlined />}
                                                    style={{ backgroundColor: '#722ed1', borderColor: '#722ed1', color: '#fff' }}
                                                    onClick={handleBulkValueAdd}
                                                    disabled={formValues.campaigns?.[campaignIndex]?.valueAdd || currentCampaignSelections.filter(i => !currentLineItems[i]?.isOrigin).length === 0}
                                                  />
                                                </Tooltip>
                                              )}
                                              <Tooltip title={`Clone Selected (${currentCampaignSelections.length})`}>
                                                <Button
                                                  type="primary"
                                                  icon={<CopyOutlined />}
                                                  disabled={currentCampaignSelections.length === 0}
                                                  onClick={handleBulkClone}
                                                />
                                              </Tooltip>
                                            </Space>
                                          </div>
                                        )}
                                    </Space>
                                    );
                                  }}
                                </Form.List>
                              </Card>
                            ))}

                            {/* Add Campaign Button */}
                            <Button
                              type="dashed"
                              onClick={handleAddCampaign}
                              icon={<PlusOutlined />}
                              disabled={!canAddMore}
                              style={{ width: '100%', height: '48px', marginTop: 16 }}
                            >
                              Add Campaign {canAddMore ? `(${campaignFields.length}/${constraints.max})` : '(Maximum reached)'}
                            </Button>
                          </Space>
                        );
                      }}
                    </Form.List>
                    <CampaignRequestorNotes
                      formValues={formValues}
                      helpers={helpers}
                    />
                  </Space>
                </Row>
              </Tabs.TabPane>
              <Tabs.TabPane tab="History" key="history" disabled={!currentCampaignOrder}>
                <CampaignOrderQueueProvider>
                  <CampaignOrderQueueHistory currentCampaignOrder={currentCampaignOrder} />
                </CampaignOrderQueueProvider>
              </Tabs.TabPane>
            </Tabs>
          </Col>
        </Row>
        <Form.Item name="geoTargets" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="complianceAudiences" hidden>
          <Input />
        </Form.Item>
      </Form>

      {/* Modal for adding multiple line items */}
      <Modal
        title="Add Multiple Line Items"
        open={isAddLineItemModalVisible}
        onOk={() => handleAddLineItems(addLineItemCount)}
        onCancel={() => { setIsAddLineItemModalVisible(false); setAddLineItemCount(1); setAddLineItemCampaignIndex(null); }}
        okText={`Add ${addLineItemCount} Line Item${addLineItemCount > 1 ? 's' : ''}`}
        okButtonProps={{ loading: isAddingLineItems, disabled: isAddingLineItems }}
        cancelButtonProps={{ disabled: isAddingLineItems }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>How many line items would you like to add?</Text>
          <InputNumber
            min={1}
            max={50}
            value={addLineItemCount}
            onChange={(value) => setAddLineItemCount(value || 1)}
            style={{ width: '100%' }}
          />
        </Space>
      </Modal>

      {/* Modal for importing line items from CSV/Excel */}
      <LineItemImportModal
        visible={isImportModalVisible}
        onClose={() => { setIsImportModalVisible(false); setImportCampaignIndex(null); }}
        onImport={handleImportLineItems}
        campaignIndex={importCampaignIndex}
        campaignDefaults={{
          campaignStartDate: importCampaignIndex !== null ? formValues.campaigns?.[importCampaignIndex]?.startDate : formValues.startDate,
          campaignEndDate: importCampaignIndex !== null ? formValues.campaigns?.[importCampaignIndex]?.endDate : formValues.endDate,
          defaultCpm: importCampaignIndex !== null ? formValues.campaigns?.[importCampaignIndex]?.cpm || 7 : 7,
          inheritedValueAdd: importCampaignIndex !== null ? formValues.campaigns?.[importCampaignIndex]?.valueAdd || false : false,
          campaignFlightSchedules: importCampaignIndex !== null ? formValues.campaigns?.[importCampaignIndex]?.flightSchedule || [] : [],
        }}
      />
    </div>
  );
};

export default GoogleApiWrapper(googleMapsConfig)(CampaignRequestorEdit);